import AddIcon from '@mui/icons-material/Add';
import { Button, SxProps, Tab, Tabs, Theme, Typography } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import { SearchInput } from '~/components/SearchInput';
import { ROUTES } from '~/constants/routes';
import { useUpdateEffect } from '~/hooks/useUpdateEffect';
import {
  useGetClosedAndRejectedCompaniesQuery,
  useGetDraftCompaniesQuery,
  useGetInProgressCompaniesQuery,
  useGetPendingCompaniesQuery,
} from '~/services/rtkQuery/company';

import { DealsInProgress } from './components/DealsInProgress';
import { DecisionsMade } from './components/DecisionsMade';
import { DraftDeals } from './components/DraftDeals';
import { PendingDeals } from './components/PendingDeals';
import styles from './styles.module.scss';

type TTab = 'draft-deals' | 'deals-in-progress' | 'decisions-made' | 'pending-deals';

const TABS: TTab[] = ['draft-deals', 'deals-in-progress', 'decisions-made', 'pending-deals'];
const SEARCH_INPUT_SX: SxProps<Theme> = { mr: '12px', width: '344px' };
const FETCH_LIMIT = 10;

export const CompaniesPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') as TTab | null;
  const [activeTabIndex, setActiveTabIndex] = useState<number | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const companyNameSearchValue = searchValue.trim().length >= 3 ? searchValue.trim() : undefined;
  const [draftCompaniesPage, setDraftCompaniesPage] = useState(1);
  const [inProgressCompaniesPage, setInProgressCompaniesPage] = useState(1);
  const [closedAndRejectedCompaniesPage, setClosedAndRejectedCompaniesPage] = useState(1);
  const [pendingCompaniesPage, setPendingCompaniesPage] = useState(1);
  const getDraftCompaniesQuery = useGetDraftCompaniesQuery({
    pageNum: draftCompaniesPage,
    perPage: FETCH_LIMIT,
    name: companyNameSearchValue,
  });
  const getInProgressCompaniesQuery = useGetInProgressCompaniesQuery({
    pageNum: inProgressCompaniesPage,
    perPage: FETCH_LIMIT,
    name: companyNameSearchValue,
  });
  const getClosedAndRejectedCompaniesQuery = useGetClosedAndRejectedCompaniesQuery({
    pageNum: closedAndRejectedCompaniesPage,
    perPage: FETCH_LIMIT,
    name: companyNameSearchValue,
  });
  const getPendingCompaniesQuery = useGetPendingCompaniesQuery({
    pageNum: pendingCompaniesPage,
    perPage: FETCH_LIMIT,
    name: companyNameSearchValue,
  });

  useEffect(() => {
    const tabIndex = TABS.findIndex(tab => tab === activeTab);

    if (tabIndex === -1) {
      setActiveTabIndex(0);
    } else {
      setActiveTabIndex(tabIndex);
    }
  }, [activeTab]);

  useUpdateEffect(() => {
    setDraftCompaniesPage(1);
    setInProgressCompaniesPage(1);
    setClosedAndRejectedCompaniesPage(1);
    setPendingCompaniesPage(1);
  }, [searchValue]);

  const handleActiveTabChange = useCallback(
    (event: React.SyntheticEvent | null, newValue: number) => {
      setSearchParams({ tab: TABS[newValue] });
    },
    [setSearchParams],
  );

  useUpdateEffect(() => {
    if (companyNameSearchValue) {
      if (
        !getDraftCompaniesQuery.isFetching &&
        getDraftCompaniesQuery.data &&
        getDraftCompaniesQuery.data.result.total > 0
      ) {
        handleActiveTabChange(null, 0);
      } else if (
        !getInProgressCompaniesQuery.isFetching &&
        getInProgressCompaniesQuery.data &&
        getInProgressCompaniesQuery.data.result.total > 0
      ) {
        handleActiveTabChange(null, 1);
      } else if (
        !getClosedAndRejectedCompaniesQuery.isFetching &&
        getClosedAndRejectedCompaniesQuery.data &&
        getClosedAndRejectedCompaniesQuery.data.result.total > 0
      ) {
        handleActiveTabChange(null, 2);
      } else if (
        !getPendingCompaniesQuery.isFetching &&
        getPendingCompaniesQuery.data &&
        getPendingCompaniesQuery.data.result.total > 0
      ) {
        handleActiveTabChange(null, 3);
      }
    }
  }, [
    companyNameSearchValue,
    getDraftCompaniesQuery.isFetching,
    getDraftCompaniesQuery.data?.result,
    getInProgressCompaniesQuery.isFetching,
    getInProgressCompaniesQuery.data?.result,
    getClosedAndRejectedCompaniesQuery.isFetching,
    getClosedAndRejectedCompaniesQuery.data?.result,
  ]);

  const handleAddNewDeal = useCallback(() => {
    navigate(ROUTES.PITCHBOOK);
  }, [navigate]);

  return (
    <div className={styles.container}>
      <div className={styles.container__heading}>
        <Typography color="var(--text-3-color)" variant="h4">
          Companies
        </Typography>
        <div className={styles['container__right-column']}>
          <SearchInput
            placeholder="Search here..."
            sx={SEARCH_INPUT_SX}
            onChange={setSearchValue}
          />
          <Button
            classes={{ startIcon: styles['container__new-button-icon'] }}
            color="secondary"
            size="small"
            startIcon={<AddIcon sx={{ width: '24px', height: '24px' }} />}
            sx={{ width: '112px', paddingLeft: '6px', paddingRight: '6px' }}
            onClick={handleAddNewDeal}
          >
            New Deal
          </Button>
        </div>
      </div>
      <Tabs
        indicatorColor="primary"
        sx={{ paddingLeft: '44px', marginBottom: '12px' }}
        value={activeTabIndex === null ? false : activeTabIndex}
        onChange={handleActiveTabChange}
      >
        <Tab label="Draft Deals" />
        <Tab label="Deals in Progress" />
        <Tab label="Decisions Made" />
        <Tab label="Pending Deals" />
      </Tabs>
      {activeTabIndex === 0 && (
        <DraftDeals
          currentPage={draftCompaniesPage}
          data={getDraftCompaniesQuery.data?.result || null}
          fetchLimit={FETCH_LIMIT}
          loading={getDraftCompaniesQuery.isFetching}
          onPageChange={setDraftCompaniesPage}
        />
      )}
      {activeTabIndex === 1 && (
        <DealsInProgress
          currentPage={inProgressCompaniesPage}
          data={getInProgressCompaniesQuery.data?.result || null}
          fetchLimit={FETCH_LIMIT}
          loading={getInProgressCompaniesQuery.isFetching}
          onPageChange={setInProgressCompaniesPage}
        />
      )}
      {activeTabIndex === 2 && (
        <DecisionsMade
          currentPage={closedAndRejectedCompaniesPage}
          data={getClosedAndRejectedCompaniesQuery.data?.result || null}
          fetchLimit={FETCH_LIMIT}
          loading={getClosedAndRejectedCompaniesQuery.isFetching}
          onPageChange={setClosedAndRejectedCompaniesPage}
        />
      )}
      {activeTabIndex === 3 && (
        <PendingDeals
          currentPage={pendingCompaniesPage}
          data={getPendingCompaniesQuery.data?.result || null}
          fetchLimit={FETCH_LIMIT}
          loading={getPendingCompaniesQuery.isFetching}
          onPageChange={setPendingCompaniesPage}
        />
      )}
    </div>
  );
};
